import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * Calculates the scrollbar width to prevent layout shift.
 * @returns {number} The width of the scrollbar in pixels.
 */
function getScrollbarWidth() {
  // More reliable method: compare window width to document width
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  
  // Fallback: create a temporary element if the above method returns 0
  if (scrollbarWidth === 0) {
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll';
    outer.style.msOverflowStyle = 'scrollbar';
    outer.style.width = '100px';
    outer.style.position = 'absolute';
    outer.style.top = '-9999px';
    document.body.appendChild(outer);

    const inner = document.createElement('div');
    inner.style.width = '100%';
    outer.appendChild(inner);

    const calculatedWidth = outer.offsetWidth - inner.offsetWidth;

    outer.parentNode?.removeChild(outer);
    return calculatedWidth;
  }

  return scrollbarWidth;
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;
  #scrollbarWidth = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;
    
    // Calculate scrollbar width BEFORE any DOM modifications
    // This must happen synchronously to accurately measure the scrollbar
    this.#scrollbarWidth = getScrollbarWidth();

    // Apply padding FIRST to prevent layout shift, then make body fixed
    // Do this synchronously before requestAnimationFrame to prevent any visual shift
    if (this.#scrollbarWidth > 0) {
      document.body.style.paddingRight = `${this.#scrollbarWidth}px`;
      // Store scrollbar width as CSS variable for potential use by other elements
      document.documentElement.style.setProperty('--scrollbar-width', `${this.#scrollbarWidth}px`);
    }

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      // Attach click handler to dialog element to handle backdrop clicks
      // Don't use capture phase so clicks inside dialog content work normally
      dialog.addEventListener('click', this.#handleClick);
      dialog.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  closeDialogOnClickOutside = (event) => {
    // This is now redundant because #handleClick handles it, 
    // but we'll keep it to avoid breaking the template call if it was reverted
    if (isClickedOutside(event, this.refs.dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  closeDialogOnEscapePress = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    // Remove event listeners from dialog element
    dialog.removeEventListener('click', this.#handleClick);
    dialog.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.paddingRight = '';
    document.documentElement.style.removeProperty('--scrollbar-width');
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick = (event) => {
    const { dialog } = this.refs;

    // When using showModal(), clicking on the backdrop (the ::backdrop pseudo-element)
    // results in the event target being the dialog element itself.
    // We simply check if the target is the dialog.
    if (event.target === dialog) {
      this.closeDialog();
    }
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          // Calculate scrollbar width before applying overflow: hidden
          const scrollbarWidth = getScrollbarWidth();
          
          // Apply scroll-lock attribute (which sets overflow: hidden on html)
          document.documentElement.setAttribute('scroll-lock', '');
          
          // Compensate for scrollbar width to prevent layout shift
          if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
            document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
          }
        } else {
          // Remove scroll-lock attribute
          document.documentElement.removeAttribute('scroll-lock');
          
          // Remove scrollbar width compensation
          document.body.style.paddingRight = '';
          document.documentElement.style.removeProperty('--scrollbar-width');
        }
      }
    }
  },
  { capture: true }
);
